import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import TenantSelector from 'components/common/TenantSelector';
import notificationService from '../../../../services/notificationService';
import { lookupService, type StatusLookup } from 'api/services/lookupService';
import { useCreateTenantUser, useUpdateTenantUser, useCheckGlobalUser, useUserTenantAccess, useTenantSummaries } from 'hooks/api';
import type { CreateTenantUserRequest, UpdateTenantUserRequest, TenantUserResponse, UserTenantAccessRecord } from 'api/types';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

// ============================================================================
// TYPES
// ============================================================================

interface TenantUserFormModalProps {
  isOpen: boolean;
  mode?: 'create' | 'edit';
  user?: TenantUserResponse | null;
  tenantId: string;
  tenantName?: string;
  onClose: () => void;
  onSaveSuccess: (savedTenantId?: string) => void;
  tabId?: string;
  moduleName?: string;
  onMinimize?: () => void;
  onRestoreModal?: (restoredMode: 'create' | 'edit', instanceId?: string, userName?: string) => void;
  createInstanceId?: string;
}

interface TenantUserFormData {
  userName: string;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  privilegeLevel: string;
  isPrimaryTenant: boolean;
  temporaryPassword: string;
  statusCode: string;
}

const INITIAL_FORM_DATA: TenantUserFormData = {
  userName: '',
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  privilegeLevel: '',
  isPrimaryTenant: false,
  temporaryPassword: '',
  statusCode: '',
};

// Levels a tenant-user form may assign, by the tenant's kind. `support` and
// `super_admin` are levels of authorized Syut personnel and exist only on the
// Syut platform tenant; a customer tenant's administration is `admin`. The Hub
// API refuses anything else (PRIVILEGE_NOT_ALLOWED_FOR_TENANT) and so does the
// database (tenant-hub-db constraints/008).
const CUSTOMER_PRIVILEGE_OPTIONS = [
  { value: 'user', label: 'User' },
  { value: 'admin', label: 'Admin' },
];
const PLATFORM_PRIVILEGE_OPTIONS = [
  { value: 'user', label: 'User' },
  { value: 'support', label: 'Support' },
  { value: 'super_admin', label: 'Super Admin' },
];
const privilegeOptionsFor = (tenantKind?: string) =>
  tenantKind === 'platform' ? PLATFORM_PRIVILEGE_OPTIONS : CUSTOMER_PRIVILEGE_OPTIONS;

// ============================================================================
// TENANT ACCESS (read-only table for edit mode)
// ============================================================================

const TenantAccessReadOnly: React.FC<{
  records: UserTenantAccessRecord[];
  loading: boolean;
  currentTenantId?: string;
}> = ({ records, loading, currentTenantId }) => {
  if (loading) {
    return <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: '0.25rem 0' }}>Loading...</div>;
  }
  if (!records.length) {
    return <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: '0.25rem 0' }}>No tenant access records.</div>;
  }
  return (
    <div style={{
      border: '1px solid var(--border-color)', borderRadius: '6px',
      overflow: 'hidden', fontSize: '0.8rem',
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--bg-secondary)' }}>
            <th style={{ textAlign: 'left', padding: '0.375rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Tenant</th>
            <th style={{ textAlign: 'left', padding: '0.375rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Privilege</th>
            <th style={{ textAlign: 'center', padding: '0.375rem 0.5rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Primary</th>
          </tr>
        </thead>
        <tbody>
          {records.map((rec) => {
            const isCurrent = rec.tenantId === currentTenantId;
            return (
              <tr key={rec.id} style={{
                borderTop: '1px solid var(--border-color)',
                background: isCurrent ? 'var(--info-bg, rgba(59, 130, 246, 0.05))' : undefined,
              }}>
                <td style={{ padding: '0.375rem 0.5rem', color: 'var(--text-primary)' }}>
                  {rec.tenant ? `${rec.tenant.code} — ${rec.tenant.name}` : rec.tenantId.slice(0, 8)}
                  {isCurrent && <span style={{ marginLeft: '0.375rem', fontSize: '0.7rem', color: 'var(--primary)', fontWeight: 600 }}>(current)</span>}
                </td>
                <td style={{ padding: '0.375rem 0.5rem' }}>
                  {rec.privilegeLevel === 'super_admin' ? <span className="badge badge-danger">Super Admin</span>
                    : rec.privilegeLevel === 'admin' ? <span className="badge badge-warning">Admin</span>
                    : rec.privilegeLevel === 'support' ? <span className="badge badge-primary">Support</span>
                    : <span className="badge badge-info">{rec.privilegeLevel || 'User'}</span>}
                </td>
                <td style={{ padding: '0.375rem 0.5rem', textAlign: 'center' }}>
                  <span className={`badge ${rec.isPrimaryTenant ? 'badge-active' : 'badge-default'}`} style={{ fontSize: '0.7rem' }}>
                    {rec.isPrimaryTenant ? 'Yes' : 'No'}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// COMPONENT
// ============================================================================

const TenantUserFormModal: React.FC<TenantUserFormModalProps> = ({
  isOpen,
  mode = 'create',
  user,
  tenantId,
  tenantName = '',
  onClose,
  onSaveSuccess,
  tabId,
  moduleName,
  onMinimize,
  onRestoreModal,
  createInstanceId,
}) => {
  const isEdit = mode === 'edit';
  const { createTenantUser, loading: creating } = useCreateTenantUser();
  const { updateTenantUser, loading: updating } = useUpdateTenantUser();
  const saving = creating || updating;
  const { minimize } = useModalMinimize();
  const formDataRef = useRef<TenantUserFormData>(INITIAL_FORM_DATA);
  const isRestoreRef = useRef(false);

  const [formData, setFormData] = useState<TenantUserFormData>(INITIAL_FORM_DATA);
  formDataRef.current = formData;
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const initialSnapshotRef = useRef('');
  const [selectedFormTenantId, setSelectedFormTenantId] = useState(tenantId);
  const selectedFormTenantIdRef = useRef(tenantId);
  selectedFormTenantIdRef.current = selectedFormTenantId;

  // Status options (fetched from API for edit mode)
  const [statusOptions, setStatusOptions] = useState<{ value: string; label: string }[]>([]);
  useEffect(() => {
    if (!isEdit || !isOpen) return;
    let cancelled = false;
    lookupService.getStatuses('USER').then((res) => {
      if (cancelled) return;
      const options = ((res.data as any)?.data || res.data || []) as StatusLookup[];
      setStatusOptions(options.map((s) => ({ value: s.code, label: s.name })));
    }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [isEdit, isOpen]);

  // ---- Global user check (create mode) ----
  const { exists: globalUserExists, user: globalUser, checking: checkingUser, check: checkGlobalUser, reset: resetGlobalCheck } = useCheckGlobalUser();

  // Fetch tenant access: edit mode uses existing user, create mode uses global user when found
  const tenantAccessUserId = isOpen
    ? isEdit && user
      ? user.userId
      : !isEdit && globalUserExists && globalUser
        ? globalUser.id
        : undefined
    : undefined;
  const { data: tenantAccess, loading: loadingAccess } = useUserTenantAccess(tenantAccessUserId);

  // Effective tenantId: internal state for create mode, prop for edit mode
  const effectiveTenantId = isEdit ? tenantId : selectedFormTenantId;

  // The privilege levels on offer follow the effective tenant's kind.
  const { data: tenantSummaries } = useTenantSummaries();
  const effectiveTenantKind = useMemo(
    () => tenantSummaries.find((t) => t.tenantId === effectiveTenantId)?.tenantKind,
    [tenantSummaries, effectiveTenantId],
  );
  const privilegeOptions = useMemo(() => privilegeOptionsFor(effectiveTenantKind), [effectiveTenantKind]);
  useEffect(() => {
    // A level chosen for one tenant kind is cleared when the selected tenant is of the other kind.
    if (!isOpen || isEdit) return;
    const current = formDataRef.current.privilegeLevel;
    if (current && !privilegeOptions.some((o) => o.value === current)) {
      setFormData((prev) => ({ ...prev, privilegeLevel: '' }));
    }
  }, [isOpen, isEdit, privilegeOptions]);

  const handleUsernameBlur = useCallback(() => {
    if (!isEdit && formData.userName.trim().length >= 2) {
      checkGlobalUser(formData.userName);
    }
  }, [isEdit, formData.userName, checkGlobalUser]);

  // Auto-fill from global user when found
  useEffect(() => {
    if (globalUserExists && globalUser && !isEdit) {
      setFormData(prev => ({
        ...prev,
        firstName: globalUser.firstName || prev.firstName,
        lastName: globalUser.lastName || prev.lastName,
        email: globalUser.email || prev.email,
      }));
    }
  }, [globalUserExists, globalUser, isEdit]);

  // Reset form on open
  useEffect(() => {
    if (isOpen) {
      if (isRestoreRef.current) {
        isRestoreRef.current = false;
        setFieldErrors({});
        return;
      }
      resetGlobalCheck();
      setSelectedFormTenantId(tenantId);
      if (isEdit && user) {
        const editData: TenantUserFormData = {
          userName: user.userName || '',
          firstName: user.firstName || '',
          middleName: user.middleName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          privilegeLevel: user.privilegeLevel || '',
          isPrimaryTenant: user.isPrimaryTenant || false,
          temporaryPassword: '',
          statusCode: user.statusCode || '',
        };
        setFormData(editData);
        initialSnapshotRef.current = JSON.stringify(editData);
      } else {
        setFormData({ ...INITIAL_FORM_DATA });
        initialSnapshotRef.current = JSON.stringify(INITIAL_FORM_DATA);
      }
      setFieldErrors({});
      setShowPassword(false);
    }
  }, [isOpen, isEdit, user]);

  const getFieldError = useCallback((field: string) => fieldErrors[field] || '', [fieldErrors]);

  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleFieldChange = useCallback((field: keyof TenantUserFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    clearFieldError(field);
  }, [clearFieldError]);

  // ---- Validation ----
  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (!isEdit && !selectedFormTenantId) errors.tenant = 'Tenant is required';

    if (!formData.userName.trim()) errors.userName = 'Username is required';
    else if (formData.userName.length < 2) errors.userName = 'Minimum 2 characters';
    else if (formData.userName.length > 100) errors.userName = 'Maximum 100 characters';

    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    else if (formData.firstName.length > 100) errors.firstName = 'Maximum 100 characters';

    if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
    else if (formData.lastName.length > 100) errors.lastName = 'Maximum 100 characters';

    if (formData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) errors.email = 'Invalid email format';
    }

    if (!formData.privilegeLevel) errors.privilegeLevel = 'Privilege level is required';

    if (!isEdit && !globalUserExists) {
      if (!formData.temporaryPassword.trim()) errors.temporaryPassword = 'Temporary password is required';
      else if (formData.temporaryPassword.length < 8) errors.temporaryPassword = 'Minimum 8 characters';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, isEdit, globalUserExists, selectedFormTenantId]);

  // ---- Save ----
  const handleSaveClick = useCallback(() => {
    if (!validate()) return;
    setConfirmSaveOpen(true);
  }, [validate]);

  const handleConfirmSave = useCallback(async () => {
    setConfirmSaveOpen(false);

    try {
      if (isEdit && user) {
        const request: UpdateTenantUserRequest = {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim() || undefined,
          privilegeLevel: formData.privilegeLevel,
          isPrimaryTenant: formData.isPrimaryTenant,
          statusCode: formData.statusCode || undefined,
        };
        await updateTenantUser(tenantId, user.userId, request, {
          onSuccess: () => {
            notificationService.toast.success(`User "${formData.userName}" updated successfully.`);
            onSaveSuccess();
          },
        });
      } else {
        const saveTenantId = selectedFormTenantIdRef.current;
        const request: CreateTenantUserRequest = {
          tenantId: saveTenantId,
          userName: formData.userName.trim(),
          firstName: formData.firstName.trim(),
          middleName: formData.middleName.trim() || undefined,
          lastName: formData.lastName.trim(),
          email: formData.email.trim() || undefined,
          privilegeLevel: formData.privilegeLevel,
          temporaryPassword: globalUserExists ? undefined : formData.temporaryPassword,
          isPrimaryTenant: formData.isPrimaryTenant,
        };
        const successMsg = globalUserExists
          ? `User "${formData.userName}" associated with tenant successfully.`
          : `User "${formData.userName}" created successfully.`;
        await createTenantUser(saveTenantId, request, {
          onSuccess: () => {
            notificationService.toast.success(successMsg);
            onSaveSuccess(saveTenantId);
          },
        });
      }
    } catch (err: any) {
      const apiMessage = err?.response?.data?.message;
      notificationService.toast.error(apiMessage || `Failed to ${isEdit ? 'update' : 'create'} user. Please try again.`);
    }
  }, [formData, isEdit, user, tenantId, createTenantUser, updateTenantUser, onSaveSuccess, globalUserExists]);

  // ---- Minimize ----
  const handleMinimize = useCallback(() => {
    const currentCreateInstanceId = createInstanceId;
    const id = isEdit
      ? `${tabId || 'tenant-user'}-form-modal-edit-${user?.userName || 'unknown'}`
      : `${tabId || 'tenant-user'}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    const title = formDataRef.current.userName
      ? `${isEdit ? 'Edit' : 'New'} User — ${formDataRef.current.userName}`
      : isEdit ? 'Edit User' : `New User (${currentCreateInstanceId || '1'})`;
    const savedFormData = { ...formDataRef.current };
    const savedSnapshot = initialSnapshotRef.current;
    const savedMode = isEdit ? 'edit' as const : 'create' as const;
    const savedTenantId = selectedFormTenantIdRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(savedFormData) !== savedSnapshot : false;
    minimize({
      id,
      title,
      moduleName: moduleName || 'User Management',
      tabId: tabId || 'tenant-user-master',
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        setSelectedFormTenantId(savedTenantId);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) onRestoreModal(
          savedMode,
          savedMode === 'create' ? currentCreateInstanceId : undefined,
          savedMode === 'edit' ? user?.userName : undefined,
        );
      },
    });
    if (onMinimize) {
      onMinimize();
    } else {
      onClose();
    }
  }, [tabId, moduleName, isEdit, user, minimize, onClose, onMinimize, onRestoreModal, createInstanceId]);

  // ---- Reset form (create mode only) ----
  const handleReset = useCallback(() => {
    setFormData({ ...INITIAL_FORM_DATA });
    setFieldErrors({});
    setShowPassword(false);
    resetGlobalCheck();
    setSelectedFormTenantId(tenantId);
    initialSnapshotRef.current = JSON.stringify(INITIAL_FORM_DATA);
  }, [resetGlobalCheck, tenantId]);

  // ---- Close with unsaved check ----
  const handleCloseAttempt = useCallback(() => {
    const dirty = JSON.stringify(formDataRef.current) !== initialSnapshotRef.current;
    if (dirty) {
      setConfirmDiscardOpen(true);
    } else {
      onClose();
    }
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        {/* Loading overlay */}
        {saving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}>
            <div style={{ width: '2.5rem', height: '2.5rem', border: '3px solid var(--border-color)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{isEdit ? 'Updating' : 'Creating'} user...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">
            <IconSystem.user size={20} />
            {isEdit ? 'Edit User' : 'Create New User'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleCloseAttempt} disabled={saving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="modal-content">
          {/* Tenant field */}
          <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              {isEdit ? (
                <>
                  <label className="form-label">Tenant</label>
                  <input
                    type="text"
                    className="form-input"
                    value={tenantName}
                    disabled
                    style={{ background: 'var(--bg-secondary)', cursor: 'not-allowed' }}
                  />
                </>
              ) : (
                <TenantSelector
                  value={selectedFormTenantId}
                  onChange={(val) => { setSelectedFormTenantId(val); clearFieldError('tenant'); }}
                  label="Tenant *"
                  placeholder="Select tenant..."
                  size="sm"
                  isClearable={false}
                  error={getFieldError('tenant')}
                />
              )}
            </div>
          </div>

          {/* Read-only ID & Created Date row — edit mode only */}
          {isEdit && user && (
            <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '1rem' }}>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">User ID</label>
                <input
                  type="text"
                  className="form-input"
                  value={user.id}
                  disabled
                  style={{ background: 'var(--bg-secondary)', cursor: 'not-allowed', fontFamily: 'monospace', fontSize: '0.8rem' }}
                />
              </div>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">Created Date</label>
                <input
                  type="text"
                  className="form-input"
                  value={new Date(user.createdAt).toLocaleString()}
                  disabled
                  style={{ background: 'var(--bg-secondary)', cursor: 'not-allowed' }}
                />
              </div>
            </div>
          )}

          {/* Existing user info banner (create mode only) */}
          {!isEdit && globalUserExists && globalUser && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 0.75rem',
              background: 'var(--info-bg, rgba(59, 130, 246, 0.08))',
              border: '1px solid var(--info-border, rgba(59, 130, 246, 0.25))',
              borderRadius: '6px',
              marginBottom: '1rem',
              fontSize: '0.8rem',
              color: 'var(--text-primary)',
            }}>
              <IconSystem.info size={16} style={{ color: 'var(--primary)', flexShrink: 0 }} />
              <span>
                <strong>{globalUser.userName}</strong> exists globally — profile fields have been auto-filled.
                This user will be associated with the selected tenant.
              </span>
            </div>
          )}

          {/* Row: Username | Email */}
          <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label className="form-label">Username *</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  className={`form-input ${getFieldError('userName') ? 'form-field-error' : ''}`}
                  value={formData.userName}
                  onChange={(e) => {
                    handleFieldChange('userName', e.target.value);
                    if (globalUserExists) resetGlobalCheck();
                  }}
                  onBlur={!isEdit ? handleUsernameBlur : undefined}
                  placeholder="Enter username"
                  maxLength={100}
                  disabled={isEdit}
                  style={isEdit ? { background: 'var(--bg-secondary)', cursor: 'not-allowed' } : undefined}
                />
                {checkingUser && (
                  <span style={{
                    position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)',
                    fontSize: '0.7rem', color: 'var(--text-muted)',
                  }}>
                    Checking...
                  </span>
                )}
              </div>
              {getFieldError('userName') && <div className="form-error-message">{getFieldError('userName')}</div>}
            </div>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label className="form-label">Email</label>
              <input
                type="email"
                className={`form-input ${getFieldError('email') ? 'form-field-error' : ''}`}
                value={formData.email}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                placeholder="Enter email address"
                disabled={!isEdit && globalUserExists}
                style={!isEdit && globalUserExists ? { background: 'var(--bg-secondary)', cursor: 'not-allowed' } : undefined}
              />
              {getFieldError('email') && <div className="form-error-message">{getFieldError('email')}</div>}
            </div>
          </div>

          {/* Row: First Name | Last Name */}
          <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label className="form-label">First Name *</label>
              <input
                type="text"
                className={`form-input ${getFieldError('firstName') ? 'form-field-error' : ''}`}
                value={formData.firstName}
                onChange={(e) => handleFieldChange('firstName', e.target.value)}
                placeholder="Enter first name"
                maxLength={100}
                disabled={!isEdit && globalUserExists}
                style={!isEdit && globalUserExists ? { background: 'var(--bg-secondary)', cursor: 'not-allowed' } : undefined}
              />
              {getFieldError('firstName') && <div className="form-error-message">{getFieldError('firstName')}</div>}
            </div>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label className="form-label">Last Name *</label>
              <input
                type="text"
                className={`form-input ${getFieldError('lastName') ? 'form-field-error' : ''}`}
                value={formData.lastName}
                onChange={(e) => handleFieldChange('lastName', e.target.value)}
                placeholder="Enter last name"
                maxLength={100}
                disabled={!isEdit && globalUserExists}
                style={!isEdit && globalUserExists ? { background: 'var(--bg-secondary)', cursor: 'not-allowed' } : undefined}
              />
              {getFieldError('lastName') && <div className="form-error-message">{getFieldError('lastName')}</div>}
            </div>
          </div>

          {/* Row: Privilege Level | Primary Tenant */}
          <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <SyutSelect
                label="Privilege Level *"
                options={privilegeOptions}
                value={formData.privilegeLevel}
                onChange={(val: string | string[] | null) => handleFieldChange('privilegeLevel', val || '')}
                placeholder="Select privilege level"
                error={getFieldError('privilegeLevel')}
                isClearable={false}
              />
            </div>
            <div className="form-group" style={{ flex: 1, marginBottom: 0, display: 'flex', alignItems: 'flex-end', paddingBottom: '0.25rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                <input
                  type="checkbox"
                  checked={formData.isPrimaryTenant}
                  onChange={(e) => handleFieldChange('isPrimaryTenant', e.target.checked)}
                  style={{ width: '1rem', height: '1rem', cursor: 'pointer' }}
                />
                Primary Tenant
              </label>
            </div>
          </div>

          {/* Temporary Password (create only, hidden for existing global users) */}
          {!isEdit && !globalUserExists && (
            <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '1rem' }}>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">Temporary Password *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className={`form-input ${getFieldError('temporaryPassword') ? 'form-field-error' : ''}`}
                    value={formData.temporaryPassword}
                    onChange={(e) => handleFieldChange('temporaryPassword', e.target.value)}
                    placeholder="Min 8 characters"
                    autoComplete="new-password"
                    style={{ paddingRight: '2.25rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(prev => !prev)}
                    title={showPassword ? 'Hide password' : 'Show password'}
                    style={{
                      position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer', padding: '0.125rem',
                      color: 'var(--text-secondary)', display: 'flex', alignItems: 'center',
                    }}
                  >
                    {showPassword ? <IconSystem.eyeOff size={16} /> : <IconSystem.eye size={16} />}
                  </button>
                </div>
                {getFieldError('temporaryPassword') && <div className="form-error-message">{getFieldError('temporaryPassword')}</div>}
              </div>
              <div style={{ flex: 1 }} />
            </div>
          )}

          {/* Status (edit only, dropdown) */}
          {isEdit && user && (
            <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '1rem' }}>
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <SyutSelect
                  label="Status"
                  options={statusOptions}
                  value={formData.statusCode}
                  onChange={(val: string | string[] | null) => handleFieldChange('statusCode', val || '')}
                  placeholder="Select status"
                  isClearable={false}
                />
              </div>
              <div style={{ flex: 1 }} />
            </div>
          )}

          {/* Last Activity info — edit mode only */}
          {isEdit && user && user.lastActivityAt && (
            <div style={{
              marginTop: '0.5rem',
              padding: '0.5rem 1rem',
              background: 'var(--bg-secondary)',
              borderRadius: '6px',
              border: '1px solid var(--border-color)',
              fontSize: '0.8rem',
            }}>
              <span style={{ color: 'var(--text-muted)' }}>Last Activity: </span>
              <span style={{ color: 'var(--text-primary)' }}>{new Date(user.lastActivityAt).toLocaleString()}</span>
            </div>
          )}

          {/* Tenant Access (edit mode + create mode when existing global user) */}
          {((isEdit && user) || (!isEdit && globalUserExists && globalUser)) && (
            <div style={{ marginTop: '1rem' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                marginBottom: '0.5rem', fontSize: '0.7rem', fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)',
              }}>
                <IconSystem.users size={14} />
                Associated Tenants
              </div>
              <TenantAccessReadOnly records={tenantAccess} loading={loadingAccess} currentTenantId={effectiveTenantId} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleCloseAttempt} disabled={saving}>
            Cancel
          </button>
          {!isEdit && (
            <button className="btn btn-secondary" onClick={handleReset} disabled={saving}>
              Reset
            </button>
          )}
          <button className="btn btn-primary" onClick={handleSaveClick} disabled={saving}>
            {isEdit ? 'Update User' : (globalUserExists ? 'Associate User' : 'Create User')}
          </button>
        </div>
      </div>

      {/* Save Confirmation */}
      <ConfirmDialog
        open={confirmSaveOpen}
        title={isEdit ? 'Update User' : (globalUserExists ? 'Associate User' : 'Create User')}
        message={isEdit
          ? `Save changes to user "${formData.userName}"?`
          : globalUserExists
            ? `User "${formData.userName}" already exists globally. Associate this user with the selected tenant?`
            : `Create user "${formData.userName}"?`
        }
        confirmLabel={isEdit ? 'Update' : (globalUserExists ? 'Associate' : 'Create')}
        cancelLabel="Cancel"
        onConfirm={handleConfirmSave}
        onCancel={() => setConfirmSaveOpen(false)}
      />

      {/* Discard Confirmation */}
      <ConfirmDialog
        open={confirmDiscardOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setConfirmDiscardOpen(false); onClose(); }}
        onCancel={() => setConfirmDiscardOpen(false)}
      />
    </>
  );
};

export default TenantUserFormModal;
